import React, { useEffect, useState } from 'react';

interface SystemSetting { key: string; value: string; updatedAt: string; }
interface LlmUsageRow { transactionType: string; model: string; callCount: number; inputTokens: number; outputTokens: number; }
interface AdminUser { userId: string; email: string | null; }

export default function AdminSystemPage() {
  const [llmUsage, setLlmUsage] = useState<LlmUsageRow[]>([]);
  const [llmUsageLoading, setLlmUsageLoading] = useState(true);

  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [settingInputs, setSettingInputs] = useState<Record<string, string>>({});
  const [settingMsgs, setSettingMsgs] = useState<Record<string, { ok: boolean; text: string }>>({});
  const [savingSettings, setSavingSettings] = useState<Record<string, boolean>>({});

  const [correctingAllLabels, setCorrectingAllLabels] = useState(false);
  const [correctAllLabelsMsg, setCorrectAllLabelsMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [promoteInput, setPromoteInput] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [promoteMsg, setPromoteMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [demotingId, setDemotingId] = useState<string | null>(null);

  async function loadAdmins() {
    try {
      const res = await fetch('/api/mgmt/admins');
      if (res.ok) setAdmins(await res.json());
    } catch {}
    setAdminsLoading(false);
  }

  async function handlePromote(e: React.FormEvent) {
    e.preventDefault();
    if (!promoteInput.trim()) return;
    setPromoting(true); setPromoteMsg(null);
    try {
      const res = await fetch(`/api/mgmt/admins/${encodeURIComponent(promoteInput.trim())}`, { method: 'POST' });
      if (res.ok) {
        setAdmins(await res.json());
        setPromoteInput('');
        setPromoteMsg({ ok: true, text: 'User promoted to admin.' });
      } else {
        const data = await res.json().catch(() => ({}));
        setPromoteMsg({ ok: false, text: (data as any).error ?? `Failed (${res.status}).` });
      }
    } catch { setPromoteMsg({ ok: false, text: 'Network error.' }); }
    setPromoting(false);
  }

  async function handleDemote(userId: string) {
    setDemotingId(userId);
    try {
      const res = await fetch(`/api/mgmt/admins/${encodeURIComponent(userId)}`, { method: 'DELETE' });
      if (res.status === 204) {
        setAdmins(prev => prev.filter(a => a.userId !== userId));
      } else {
        const data = await res.json().catch(() => ({}));
        alert((data as any).error ?? `Failed (${res.status}).`);
      }
    } catch { alert('Network error.'); }
    setDemotingId(null);
  }

  useEffect(() => {
    loadAdmins();
    fetch('/api/mgmt/settings')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: SystemSetting[]) => {
        setSettings(data);
        const inputs: Record<string, string> = {};
        data.forEach(s => { inputs[s.key] = s.value; });
        setSettingInputs(inputs);
      })
      .catch(() => {});

    fetch('/api/mgmt/llm-usage')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: LlmUsageRow[]) => setLlmUsage(data))
      .catch(() => {})
      .finally(() => setLlmUsageLoading(false));
  }, []);

  async function handleCorrectAllLabels() {
    setCorrectingAllLabels(true); setCorrectAllLabelsMsg(null);
    try {
      const res = await fetch('/api/mgmt/correct-all-labels', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setCorrectAllLabelsMsg({ ok: true, text: `Queued correction for ${data.usersEnqueued} user(s).` });
      } else {
        const data = await res.json().catch(() => ({}));
        setCorrectAllLabelsMsg({ ok: false, text: (data as any).error ?? `Failed (${res.status}).` });
      }
    } catch { setCorrectAllLabelsMsg({ ok: false, text: 'Network error.' }); }
    finally { setCorrectingAllLabels(false); }
  }

  async function handleSaveSetting(key: string) {
    setSavingSettings(prev => ({ ...prev, [key]: true }));
    setSettingMsgs(prev => ({ ...prev, [key]: { ok: false, text: '' } }));
    try {
      const res = await fetch(`/api/mgmt/settings/${key}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: settingInputs[key] }),
      });
      setSettingMsgs(prev => ({
        ...prev,
        [key]: res.ok ? { ok: true, text: 'Saved.' } : { ok: false, text: 'Save failed.' },
      }));
    } catch {
      setSettingMsgs(prev => ({ ...prev, [key]: { ok: false, text: 'Save failed.' } }));
    } finally {
      setSavingSettings(prev => ({ ...prev, [key]: false }));
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>System</h1>
      </div>

      {/* ── LLM usage ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div className="card-label" style={{ marginBottom: 12 }}>LLM usage — last 30 days</div>
        {llmUsageLoading ? (
          <div className="skeleton skeleton-text" style={{ width: 200 }} />
        ) : llmUsage.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No usage recorded yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Transaction', 'Model', 'Calls', 'Input tokens', 'Output tokens', 'Est. cost (USD)'].map(h => (
                  <th key={h} style={{ textAlign: h === 'Transaction' || h === 'Model' ? 'left' : 'right', padding: '4px 10px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {llmUsage.map((row, i) => {
                const inputRate  = row.model?.includes('sonnet') ? 3.00  : 0.80;
                const outputRate = row.model?.includes('sonnet') ? 15.00 : 4.00;
                const cost = ((row.inputTokens ?? 0) / 1_000_000) * inputRate + ((row.outputTokens ?? 0) / 1_000_000) * outputRate;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 10px' }}>{row.transactionType}</td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{row.model}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.callCount?.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.inputTokens?.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.outputTokens?.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>${cost.toFixed(4)}</td>
                  </tr>
                );
              })}
              {llmUsage.length > 1 && (() => {
                const totals = llmUsage.reduce((acc, r) => {
                  const inputRate  = r.model?.includes('sonnet') ? 3.00  : 0.80;
                  const outputRate = r.model?.includes('sonnet') ? 15.00 : 4.00;
                  return {
                    calls: acc.calls + (r.callCount ?? 0),
                    input: acc.input + (r.inputTokens ?? 0),
                    output: acc.output + (r.outputTokens ?? 0),
                    cost: acc.cost + ((r.inputTokens ?? 0) / 1_000_000) * inputRate + ((r.outputTokens ?? 0) / 1_000_000) * outputRate,
                  };
                }, { calls: 0, input: 0, output: 0, cost: 0 });
                return (
                  <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 600 }}>
                    <td style={{ padding: '6px 10px' }} colSpan={2}>Total</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{totals.calls.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{totals.input.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{totals.output.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>${totals.cost.toFixed(4)}</td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* ── Label maintenance ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div className="card-label" style={{ marginBottom: 12 }}>Label maintenance</div>
        <div style={{ marginBottom: 8, fontWeight: 600 }}>Correct label numbering — all users</div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Enqueues a label-numbering correction job for every user who has Gmail labels stored.
          Renames mispositioned labels (e.g. OTP from a numbered slot to the unnumbered position)
          and migrates email_labels records to the corrected IDs.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={handleCorrectAllLabels} disabled={correctingAllLabels}>
            {correctingAllLabels ? 'Queueing…' : 'Correct label numbering for all users'}
          </button>
          {correctAllLabelsMsg && (
            <span style={{ fontSize: 13, color: correctAllLabelsMsg.ok ? 'var(--success)' : 'var(--danger)' }}>
              {correctAllLabelsMsg.text}
            </span>
          )}
        </div>
      </div>

      {/* ── Admins ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div className="card-label" style={{ marginBottom: 12 }}>Admins</div>
        {adminsLoading ? (
          <div className="skeleton skeleton-text" style={{ width: 200 }} />
        ) : admins.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>No admins found.</p>
        ) : (
          <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%', marginBottom: 16 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['User ID', 'Email', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '4px 10px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {admins.map(a => (
                <tr key={a.userId} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 12 }}>{a.userId}</td>
                  <td style={{ padding: '6px 10px' }}>{a.email ?? <span style={{ color: 'var(--text-secondary)' }}>—</span>}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 12, padding: '2px 8px' }}
                      disabled={demotingId === a.userId}
                      onClick={() => handleDemote(a.userId)}
                    >
                      {demotingId === a.userId ? 'Removing…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <form onSubmit={handlePromote} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input
            className="search-input"
            type="text"
            placeholder="User ID to promote"
            value={promoteInput}
            onChange={e => setPromoteInput(e.target.value)}
            style={{ width: 280 }}
          />
          <button className="btn-secondary" type="submit" disabled={promoting || !promoteInput.trim()}>
            {promoting ? 'Promoting…' : 'Promote to admin'}
          </button>
          {promoteMsg && (
            <span style={{ fontSize: 13, color: promoteMsg.ok ? 'var(--success)' : 'var(--danger)' }}>
              {promoteMsg.text}
            </span>
          )}
        </form>
      </div>

      {/* ── System settings ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div className="card-label" style={{ marginBottom: 12 }}>System settings</div>
        {settings.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No settings found.</p>
        ) : (
          <>
          {settings.find(s => s.key === 'llm_circuit_breaker_open')?.value?.toLowerCase() === 'true' && (
            <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(220,38,38,0.08)', borderLeft: '3px solid var(--danger)', borderRadius: 4, fontSize: 13, color: 'var(--danger)', fontWeight: 500 }}>
              LLM circuit breaker is open — Anthropic API calls are paused. Reset from the Monitoring page.
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
            <tbody>
              {settings.map(s => (
                <tr key={s.key} style={s.key === 'llm_circuit_breaker_open' && s.value?.toLowerCase() === 'true' ? { background: 'rgba(220,38,38,0.06)' } : undefined}>
                  <td style={{ padding: '5px 12px 5px 0', color: 'var(--text-secondary)', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{s.key}</td>
                  <td style={{ padding: '5px 8px 5px 0', verticalAlign: 'middle' }}>
                    <input
                      className="search-input"
                      type="text"
                      value={settingInputs[s.key] ?? s.value}
                      onChange={e => setSettingInputs(prev => ({ ...prev, [s.key]: e.target.value }))}
                      style={{ width: 140 }}
                    />
                  </td>
                  <td style={{ padding: '5px 8px 5px 0', verticalAlign: 'middle' }}>
                    <button
                      className="btn-secondary"
                      onClick={() => handleSaveSetting(s.key)}
                      disabled={!!savingSettings[s.key]}
                    >
                      {savingSettings[s.key] ? 'Saving…' : 'Save'}
                    </button>
                  </td>
                  <td style={{ padding: '5px 0', verticalAlign: 'middle' }}>
                    {settingMsgs[s.key]?.text && (
                      <span style={{ color: settingMsgs[s.key].ok ? 'var(--success)' : 'var(--danger)' }}>
                        {settingMsgs[s.key].text}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>
    </>
  );
}
